function appendScript(id: string, src: string): HTMLScriptElement {
  const existing = document.getElementById(id) as HTMLScriptElement | null
  if (existing)
    return existing

  const script = document.createElement('script')
  script.id = id
  script.async = true
  script.src = src
  document.head.appendChild(script)
  return script
}

function loadFacebookPixel(): void {
  if ((window as any).fbq)
    return

  const fbq = function (...args: any[]) {
    if ((fbq as any).callMethod)
      (fbq as any).callMethod(...args)
    else
      (fbq as any).queue.push(args)
  } as any

  ;(window as any).fbq = fbq
  ;(window as any)._fbq = fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  appendScript('glouton-facebook-pixel', 'https://connect.facebook.net/en_US/fbevents.js')
  fbq('consent', 'grant')
  fbq('init', '2090161414562335')
  fbq('track', 'PageView')
}

function loadGoogleTagManager(): void {
  if (document.getElementById('glouton-google-tag-manager'))
    return

  const dataLayer = (window as any).dataLayer = (window as any).dataLayer || []
  dataLayer.push({
    'gtm.start': Date.now(),
    'event': 'gtm.js',
  })
  appendScript('glouton-google-tag-manager', 'https://www.googletagmanager.com/gtm.js?id=GTM-PQJTXJ6S')
}

function updateGoogleConsent(command: 'default' | 'update', googleAnalytics: boolean, advertising: boolean): void {
  const dataLayer = (window as any).dataLayer = (window as any).dataLayer || []
  const gtag = (window as any).gtag = (window as any).gtag || function () {
    dataLayer.push(arguments)
  }

  gtag('consent', command, {
    analytics_storage: googleAnalytics ? 'granted' : 'denied',
    ad_storage: advertising ? 'granted' : 'denied',
    ad_user_data: advertising ? 'granted' : 'denied',
    ad_personalization: advertising ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    ...(command === 'default' ? { wait_for_update: 500 } : {}),
  })
}

function loadTikTokPixel(): void {
  if ((window as any).ttq)
    return

  const key = 'ttq'
  const ttq: any = (window as any)[key] = []
  const methods = [
    'page',
    'track',
    'identify',
    'instances',
    'debug',
    'on',
    'off',
    'once',
    'ready',
    'alias',
    'group',
    'enableCookie',
    'disableCookie',
    'holdConsent',
    'revokeConsent',
    'grantConsent',
  ]

  ttq.methods = methods
  ttq.setAndDefer = (target: any, method: string) => {
    target[method] = (...args: any[]) => target.push([method, ...args])
  }
  methods.forEach(method => ttq.setAndDefer(ttq, method))
  ttq.instance = (pixelId: string) => {
    ttq._i = ttq._i || {}
    const instance = ttq._i[pixelId] || []
    methods.forEach(method => ttq.setAndDefer(instance, method))
    return instance
  }
  ttq.load = (pixelId: string, options?: Record<string, any>) => {
    const baseUrl = 'https://ttapi.glouton.xyz/load/data.js'
    ttq._i = ttq._i || {}
    ttq._i[pixelId] = []
    ttq._i[pixelId]._u = baseUrl
    ttq._t = ttq._t || {}
    ttq._t[pixelId] = Date.now()
    ttq._o = ttq._o || {}
    ttq._o[pixelId] = options || {}

    const script = appendScript('glouton-tiktok-pixel', `${baseUrl}?sdkid=${pixelId}&lib=${key}`)
    script.onerror = () => {
      script.remove()
      appendScript('glouton-tiktok-pixel-fallback', `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${pixelId}&lib=${key}`)
    }
  }

  ttq.load('CTJKBTJC77U8UG3M5DJG')
  ttq.grantConsent()
  ttq.page()
}

function loadAdvertisingScripts(): void {
  loadFacebookPixel()
  loadTikTokPixel()
}

function deleteCookies(prefixes: string[]): void {
  const cookieNames = document.cookie
    .split(';')
    .map(cookie => cookie.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name))
    .filter(name => prefixes.some(prefix => name.startsWith(prefix)))

  for (const name of cookieNames) {
    document.cookie = `${name}=; Max-Age=0; path=/`
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.glouton.app`
  }
}

function revokeAdvertisingConsent(): void {
  const fbq = (window as any).fbq
  if (typeof fbq === 'function')
    fbq('consent', 'revoke')

  const ttq = (window as any).ttq
  if (typeof ttq?.revokeConsent === 'function')
    ttq.revokeConsent()

  deleteCookies(['_fbp', '_fbc', '_ttp', '_gcl'])
}

function revokeGoogleAnalyticsConsent(): void {
  deleteCookies(['_ga', '_gid', '_gat'])
}

export default defineNuxtPlugin(async () => {
  const route = useRoute()
  const consentStore = useConsentStore()
  await consentStore.initialize({ automated: route.query.low === 'yes' })

  updateGoogleConsent('default', consentStore.googleAnalyticsAllowed, consentStore.advertisingAllowed)

  if (consentStore.googleAnalyticsAllowed || consentStore.advertisingAllowed)
    loadGoogleTagManager()
  if (consentStore.advertisingAllowed)
    loadAdvertisingScripts()
  else
    revokeAdvertisingConsent()
  if (!consentStore.googleAnalyticsAllowed)
    revokeGoogleAnalyticsConsent()

  watch(
    () => route.query.low,
    low => consentStore.setAutomatedMode(low === 'yes'),
  )

  watch(
    [() => consentStore.googleAnalyticsAllowed, () => consentStore.advertisingAllowed],
    ([googleAnalytics, advertising], [previousGoogleAnalytics, previousAdvertising]) => {
      updateGoogleConsent('update', googleAnalytics, advertising)

      if (googleAnalytics || advertising)
        loadGoogleTagManager()
      if (advertising)
        loadAdvertisingScripts()
      else
        revokeAdvertisingConsent()
      if (!googleAnalytics)
        revokeGoogleAnalyticsConsent()

      const withdrewConsent = (previousGoogleAnalytics && !googleAnalytics)
        || (previousAdvertising && !advertising)
      if (withdrewConsent)
        window.location.reload()
    },
  )
})
